/**
 * Flutter SDK — dash data layer (feature #185).
 *
 * Thin typed wrapper over `apiClient` for the Flutter SDK endpoints:
 *
 *   GET    /api/v1/flutter-sdk/config?app_id&platform&sdk_version
 *   GET    /api/v1/flutter-sdk/apps
 *   POST   /api/v1/flutter-sdk/apps
 *   PATCH  /api/v1/flutter-sdk/apps/:id
 *   DELETE /api/v1/flutter-sdk/apps/:id
 *   POST   /api/v1/flutter-sdk/installations
 *   GET    /api/v1/flutter-sdk/installations?app_id&limit
 *
 * Mirrors the server view shape in apps/api/src/routes/flutter-sdk.ts.
 * Pure helpers (validators, formatters, summaries) stay dependency-free so
 * they can be unit-tested without touching the network.
 */

import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Enum + constant mirrors
// ---------------------------------------------------------------------------

export const FLUTTER_CHANNELS = ["stable", "beta", "dev"] as const;
export type FlutterChannel = (typeof FLUTTER_CHANNELS)[number];

export const FLUTTER_PLATFORMS = ["ios", "android"] as const;
export type FlutterPlatform = (typeof FLUTTER_PLATFORMS)[number];

export const APP_NAME_MAX_BYTES = 160;
export const BUNDLE_ID_MAX_BYTES = 200;
export const CONFIG_MAX_BYTES = 16 * 1024;

export const APP_ID_RE = /^fla_[A-Za-z0-9_.:-]{1,128}$/;
const RDNS_RE =
	/^[A-Za-z][A-Za-z0-9_-]*(?:\.[A-Za-z][A-Za-z0-9_-]*)+$/;
const SEMVER_RE =
	/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;

// ---------------------------------------------------------------------------
// Types — mirror the server view shape.
// ---------------------------------------------------------------------------

export interface FlutterSdkAppView {
	id: string;
	app_name: string;
	ios_bundle_id: string | null;
	android_package: string | null;
	flutter_channel: FlutterChannel;
	config: Record<string, unknown>;
	active: boolean;
	created_at: number;
	updated_at: number;
}

export interface FlutterSdkInstallationView {
	id: string;
	app_id: string;
	platform: FlutterPlatform;
	install_id: string;
	sdk_version: string;
	flutter_version: string;
	created_at: number;
}

export interface FlutterSdkAppsResponse {
	count: number;
	items: FlutterSdkAppView[];
}

export interface FlutterSdkInstallationsResponse {
	count: number;
	items: FlutterSdkInstallationView[];
}

export interface FlutterSdkConfigResponse {
	app_id: string;
	app_name: string;
	platform: FlutterPlatform;
	bundle_id: string;
	flutter_channel: FlutterChannel;
	sdk_version: string | null;
	config: Record<string, unknown>;
}

export interface CreateAppArgs {
	app_name: string;
	ios_bundle_id?: string | null;
	android_package?: string | null;
	flutter_channel?: FlutterChannel;
	config?: Record<string, unknown>;
	active?: boolean;
}

export interface UpdateAppArgs {
	app_name?: string;
	ios_bundle_id?: string | null;
	android_package?: string | null;
	flutter_channel?: FlutterChannel;
	config?: Record<string, unknown>;
	active?: boolean;
}

export interface CreateInstallationArgs {
	app_id: string;
	platform: FlutterPlatform;
	sdk_version: string;
	flutter_version: string;
	install_id: string;
}

// ---------------------------------------------------------------------------
// Guards + validators — mirror the server rules so the UI can short-circuit
// bad input before the round trip.
// ---------------------------------------------------------------------------

function byteLength(v: string): number {
	return new TextEncoder().encode(v).length;
}

export function isFlutterChannel(v: unknown): v is FlutterChannel {
	return (
		typeof v === "string" &&
		(FLUTTER_CHANNELS as readonly string[]).includes(v)
	);
}

export function isFlutterPlatform(v: unknown): v is FlutterPlatform {
	return (
		typeof v === "string" &&
		(FLUTTER_PLATFORMS as readonly string[]).includes(v)
	);
}

export function isValidAppId(v: unknown): v is string {
	return typeof v === "string" && APP_ID_RE.test(v);
}

export function isValidReverseDns(v: unknown): v is string {
	if (typeof v !== "string") return false;
	if (v.length === 0 || v.length > BUNDLE_ID_MAX_BYTES) return false;
	return RDNS_RE.test(v);
}

export function isValidIosBundleId(v: unknown): v is string {
	return isValidReverseDns(v);
}

export function isValidAndroidPackage(v: unknown): v is string {
	return isValidReverseDns(v);
}

export function isValidSemVer(v: unknown): v is string {
	if (typeof v !== "string") return false;
	if (v.length === 0 || v.length > 64) return false;
	return SEMVER_RE.test(v);
}

export function isValidAppName(v: unknown): v is string {
	if (typeof v !== "string") return false;
	const trimmed = v.trim();
	if (trimmed.length === 0) return false;
	return byteLength(trimmed) <= APP_NAME_MAX_BYTES;
}

export function isValidConfigObject(v: unknown): v is Record<string, unknown> {
	if (v === null || typeof v !== "object" || Array.isArray(v)) return false;
	try {
		const encoded = JSON.stringify(v);
		if (typeof encoded !== "string") return false;
		return byteLength(encoded) <= CONFIG_MAX_BYTES;
	} catch {
		return false;
	}
}

export function hasAtLeastOnePlatform(
	iosBundleId: string | null | undefined,
	androidPackage: string | null | undefined,
): boolean {
	return (
		(typeof iosBundleId === "string" && iosBundleId.length > 0) ||
		(typeof androidPackage === "string" && androidPackage.length > 0)
	);
}

// ---------------------------------------------------------------------------
// Config JSON textarea parsing — the dash accepts a JSON string from the
// operator and we need to surface parse errors inline before submitting.
// ---------------------------------------------------------------------------

export type ParsedConfig =
	| { ok: true; value: Record<string, unknown> }
	| { ok: false; error: "invalid_json" | "expected_object" | "too_large" };

export function parseConfigInput(raw: string): ParsedConfig {
	const trimmed = (raw ?? "").trim();
	if (trimmed.length === 0) {
		return { ok: true, value: {} };
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(trimmed);
	} catch {
		return { ok: false, error: "invalid_json" };
	}
	if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
		return { ok: false, error: "expected_object" };
	}
	const encoded = JSON.stringify(parsed);
	if (byteLength(encoded) > CONFIG_MAX_BYTES) {
		return { ok: false, error: "too_large" };
	}
	return { ok: true, value: parsed as Record<string, unknown> };
}

// ---------------------------------------------------------------------------
// Summary — platform counts + active count used by the dash header.
// ---------------------------------------------------------------------------

export interface FlutterSdkSummary {
	total: number;
	active: number;
	ios: number;
	android: number;
	cross_platform: number;
}

export function summarizeApps(
	apps: readonly FlutterSdkAppView[],
): FlutterSdkSummary {
	let active = 0;
	let ios = 0;
	let android = 0;
	let cross = 0;
	for (const a of apps) {
		if (a.active) active += 1;
		const hasIos = !!a.ios_bundle_id;
		const hasAnd = !!a.android_package;
		if (hasIos && hasAnd) {
			cross += 1;
			ios += 1;
			android += 1;
		} else if (hasIos) {
			ios += 1;
		} else if (hasAnd) {
			android += 1;
		}
	}
	return { total: apps.length, active, ios, android, cross_platform: cross };
}

// ---------------------------------------------------------------------------
// Deterministic sort — newest first, id ascending tiebreak.
// ---------------------------------------------------------------------------

export function sortApps(
	apps: readonly FlutterSdkAppView[],
): FlutterSdkAppView[] {
	return [...apps].sort((a, b) => {
		if (b.created_at !== a.created_at) return b.created_at - a.created_at;
		if (a.id < b.id) return -1;
		if (a.id > b.id) return 1;
		return 0;
	});
}

export function sortInstallations(
	rows: readonly FlutterSdkInstallationView[],
): FlutterSdkInstallationView[] {
	return [...rows].sort((a, b) => {
		if (b.created_at !== a.created_at) return b.created_at - a.created_at;
		if (a.id < b.id) return -1;
		if (a.id > b.id) return 1;
		return 0;
	});
}

// ---------------------------------------------------------------------------
// Remote bindings
// ---------------------------------------------------------------------------

const BASE = "/flutter-sdk";

export async function fetchApps(): Promise<FlutterSdkAppsResponse> {
	return apiClient.get<FlutterSdkAppsResponse>(`${BASE}/apps`);
}

export async function createApp(
	args: CreateAppArgs,
): Promise<{ app: FlutterSdkAppView }> {
	const body: Record<string, unknown> = {
		app_name: args.app_name,
	};
	if (args.ios_bundle_id !== undefined)
		body.ios_bundle_id = args.ios_bundle_id;
	if (args.android_package !== undefined)
		body.android_package = args.android_package;
	if (args.flutter_channel !== undefined)
		body.flutter_channel = args.flutter_channel;
	if (args.config !== undefined) body.config = args.config;
	if (args.active !== undefined) body.active = args.active;
	return apiClient.post<{ app: FlutterSdkAppView }>(`${BASE}/apps`, body);
}

export async function updateApp(
	id: string,
	args: UpdateAppArgs,
): Promise<{ app: FlutterSdkAppView }> {
	const body: Record<string, unknown> = {};
	if (args.app_name !== undefined) body.app_name = args.app_name;
	if (args.ios_bundle_id !== undefined)
		body.ios_bundle_id = args.ios_bundle_id;
	if (args.android_package !== undefined)
		body.android_package = args.android_package;
	if (args.flutter_channel !== undefined)
		body.flutter_channel = args.flutter_channel;
	if (args.config !== undefined) body.config = args.config;
	if (args.active !== undefined) body.active = args.active;
	return apiClient.patch<{ app: FlutterSdkAppView }>(
		`${BASE}/apps/${encodeURIComponent(id)}`,
		body,
	);
}

export async function deleteApp(
	id: string,
): Promise<{ ok: true; id: string }> {
	return apiClient.delete<{ ok: true; id: string }>(
		`${BASE}/apps/${encodeURIComponent(id)}`,
	);
}

export async function resolveConfig(args: {
	app_id: string;
	platform: FlutterPlatform;
	sdk_version?: string;
}): Promise<FlutterSdkConfigResponse> {
	return apiClient.get<FlutterSdkConfigResponse>(`${BASE}/config`, {
		query: {
			app_id: args.app_id,
			platform: args.platform,
			sdk_version: args.sdk_version ?? undefined,
		},
	});
}

export async function createInstallation(
	args: CreateInstallationArgs,
): Promise<{ installation: FlutterSdkInstallationView }> {
	return apiClient.post<{ installation: FlutterSdkInstallationView }>(
		`${BASE}/installations`,
		{
			app_id: args.app_id,
			platform: args.platform,
			sdk_version: args.sdk_version,
			flutter_version: args.flutter_version,
			install_id: args.install_id,
		},
	);
}

export async function fetchInstallations(args: {
	app_id?: string;
	limit?: number;
}): Promise<FlutterSdkInstallationsResponse> {
	return apiClient.get<FlutterSdkInstallationsResponse>(
		`${BASE}/installations`,
		{
			query: {
				app_id: args.app_id ?? undefined,
				limit: args.limit ?? undefined,
			},
		},
	);
}

// ---------------------------------------------------------------------------
// Formatters — light & dependency free for reuse in row components.
// ---------------------------------------------------------------------------

export function formatTimestamp(unixSec: number | null | undefined): string {
	if (
		unixSec === null ||
		unixSec === undefined ||
		!Number.isFinite(unixSec) ||
		unixSec <= 0
	) {
		return "Never";
	}
	const d = new Date(unixSec * 1000);
	return d.toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function formatInteger(n: number | null | undefined): string {
	if (n === null || n === undefined || !Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

export function formatChannelLabel(channel: FlutterChannel): string {
	switch (channel) {
		case "stable":
			return "Stable";
		case "beta":
			return "Beta";
		case "dev":
			return "Dev";
	}
}

export function formatPlatformLabel(platform: FlutterPlatform): string {
	return platform === "ios" ? "iOS" : "Android";
}

export function describeAppPlatforms(
	app: Pick<FlutterSdkAppView, "ios_bundle_id" | "android_package">,
): "ios" | "android" | "cross" | "none" {
	const hasIos = !!app.ios_bundle_id;
	const hasAnd = !!app.android_package;
	if (hasIos && hasAnd) return "cross";
	if (hasIos) return "ios";
	if (hasAnd) return "android";
	return "none";
}

export function formatConfigPreview(
	value: Record<string, unknown>,
	limit = 80,
): string {
	const keys = Object.keys(value);
	if (keys.length === 0) return "{}";
	try {
		const encoded = JSON.stringify(value);
		if (encoded.length <= limit) return encoded;
		return `${encoded.slice(0, limit - 3)}...`;
	} catch {
		return `{${keys.length} keys}`;
	}
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const flutterSdkQueryKeys = {
	all: ["flutter-sdk"] as const,
	apps: () => [...flutterSdkQueryKeys.all, "apps"] as const,
	installations: (appId: string | null) =>
		[...flutterSdkQueryKeys.all, "installations", appId ?? "__all__"] as const,
};
